import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import {
  btag,
  pushToDataLayer,
  SessionStorageManager,
  SiteConfigManager,
  useApplicationState,
} from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes } from '@atlas/routing';
import { DefaultPopup, HeaderTitle, Label } from '@atlas/ui';

import StaticBlock from 'common/StaticBlock/StaticBlock';
import Navigation from 'components/Auth/Navigation';
import { usePostHog, useRegistrationPostHog } from 'hooks/usePostHog';
import {
  ACCOUNT_STATUSES,
  OAUTH_FLOW_TYPES,
  POPUP_SIGN_UP_DELAY_STORAGE_KEYS,
} from 'src/common/constants';
import { useSignupPopup } from 'src/components/Auth/useSignUpPopup';
import useSocialProviders from 'src/components/Auth/useSocialProviders';
import { customSignupFormRules } from 'src/helpers/customFormRules';

import useAutoAdvancePrefilledSteps from './hooks/useAutoAdvancePrefilledSteps';
import useOAuthRegistrationForm from './hooks/useOAuthRegistrationForm';
import useTouchPrefilledFields from './hooks/useTouchPrefilledFields';
import { changeAsyncToDeferRules } from './change-async-to-defer-rules';
import { getMultistepConfig } from './getMultistepConfig';
import { resolveDefaultPageAfterRegistration } from './resolveDefaultPageAfterRegistration';
import { GTM_TRACKED_FIELDS, gtmFieldChangesHandler } from './SignUp';
import SignUpFormBody from './SignUpFormBody';
import useRegistrationForm from './useRegistrationForm';
import useSetMultiStepForPartiallyRegistered from './useSetMultiStepForPartiallyRegistered';
import useSignUpAction from './useSignUpAction';
import useSignupLeavePopupGuard from './useSignupLeavePopupGuard';
import Logo from 'common/Widgets/Logo/Logo';

const { CLIENT_PROFILE } = COMPONENT_TYPES;
const { payment } = clientProfileRoutes;
const { deposit } = paymentRoutes;

const getIsLeavePopupEnabled = ({ isUserFullyRegistered, optimized, search, signUpPopup }) =>
  !isUserFullyRegistered && !optimized && (signUpPopup || search !== '?cmd=signup');

const SignUp = ({ optimized }) => {
  const bypassLeaveBlockRef = useRef(false);
  const formRef = useRef(null);
  const [isSubmittedForm, setSubmittedForm] = useState(false);
  const { account, authenticated, resolved } = useApplicationState();
  const { closeSignupPopup } = useSignupPopup();
  const history = useHistory();
  const leaveAfterFirstStep = SiteConfigManager.getConfig(
    'config.config.registration.close_multistep_after_first_step',
    false,
  );

  const enableSignUpDelay = SiteConfigManager.getConfig('config.config.registration.mobile_popup');
  const isShownProgressBar = SiteConfigManager.getConfig(
    'config.config.registration.showRegistrationProgressBar',
    true,
  );
  const postHog = usePostHog();

  const { getActiveFormsWithOAuth, isOAuthRegistrationRef } = useOAuthRegistrationForm();

  const {
    activeStep,
    activeType,
    resetActiveForm,
    setActiveStep,
    setActiveType,
    stepSubmit,
    stepsAmount,
    submit,
    types,
  } = useRegistrationForm({ onSubmitStart: () => setSubmittedForm(true), getActiveFormsWithOAuth });

  const { handleBackClick, handleLeave, handleNextStep } = useRegistrationPostHog({
    activeStep,
    cxFromParam: null,
    cxFromPath: null,
    postHog,
    stepsAmount,
  });

  const isMultiStepFlow = activeType?.templateName === 'MULTI_STEP';
  const isUserFullyRegistered =
    resolved && authenticated && account.status !== ACCOUNT_STATUSES.PARTIALLY_REGISTERED;

  useSetMultiStepForPartiallyRegistered({
    accountStatus: account.status,
    activeType,
    setActiveType,
    types,
  });

  useEffect(() => {
    const isFormCompletedOrFullyRegistered =
      activeType?.completed === true || account.status !== ACCOUNT_STATUSES.PARTIALLY_REGISTERED;

    const shouldRedirect =
      !isSubmittedForm && resolved && authenticated && isFormCompletedOrFullyRegistered;

    if (shouldRedirect) {
      // prevent leave popup during internal auth/completion redirect
      bypassLeaveBlockRef.current = true;
      window.location.href = '/';
    }
  }, [isSubmittedForm, resolved, authenticated, activeType?.completed, account.status]);

  useEffect(() => {
    pushToDataLayer('registration_start', { action: 'start' }, btag);
  }, []);

  const signUpPopup = optimized
    ? false
    : SiteConfigManager.getConfig('config.config.registration.showInPopup', false);

  useEffect(() => {
    const activeStepFields = activeType?.steps[activeStep]?.fields;
    if (
      activeStepFields &&
      activeStepFields.length === 1 &&
      activeStepFields[0].component === 'SMS_CODE'
    ) {
      pushToDataLayer('OTP_requested', btag);
    }
  }, [activeStep, activeType?.steps]);

  useEffect(() => {
    const nextDayMs = dayjs().add(1, 'd').valueOf();
    if (enableSignUpDelay && location.search.includes('cmd=signup')) {
      SessionStorageManager.setItem(POPUP_SIGN_UP_DELAY_STORAGE_KEYS.NEXT_SHOW, nextDayMs);
    }
  }, [enableSignUpDelay]);

  const isLeavePopupEnabled = getIsLeavePopupEnabled({
    isUserFullyRegistered,
    optimized,
    search: location.search,
    signUpPopup,
  });

  useSignupLeavePopupGuard({
    enabled: isLeavePopupEnabled,
    history,
    isMultiStepFlow,
    isPartiallyRegistered: account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED,
    onLeave: handleLeave,
    popupType: 'signupPopup',
    shouldBypassBlock: () => bypassLeaveBlockRef.current,
  });

  const defaultPageAfterRegistration = SiteConfigManager.getConfig(
    'config.config.links.defaultPageAfterRegistration',
  );

  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);

  const isLastStep = activeType && activeStep === activeType.steps.length - 1;
  const isFirstStep = activeStep === 0;
  const isBackButtonVisible = activeStep > (isMultiStepFlow ? 1 : 0);
  const { headerTitle, showSignInLink } = getMultistepConfig(isMultiStepFlow, isFirstStep, account);

  const onBackButtonClick = useCallback(() => {
    handleBackClick();
    setActiveStep(activeStep - 1);
  }, [setActiveStep, activeStep, handleBackClick]);

  useTouchPrefilledFields({
    activeStep,
    activeType,
    formRef,
    isOAuthRegistration: isOAuthRegistrationRef.current,
  });

  useAutoAdvancePrefilledSteps({
    activeType,
    isOAuthRegistration: isOAuthRegistrationRef.current,
    setActiveStep,
  });

  const redirectAfterRegistration = useCallback(() => {
    const pageAfterRegistration = resolveDefaultPageAfterRegistration(defaultPageAfterRegistration);

    const isMultiStepFirstStepExit = isMultiStepFlow && isFirstStep && leaveAfterFirstStep;

    const redirectPath = isMultiStepFirstStepExit
      ? '/'
      : pageAfterRegistration || `${clientProfilePath}/${payment}/${deposit}`;

    // allow post-submit internal redirect without triggering leave popup
    bypassLeaveBlockRef.current = true;

    history.push(redirectPath);
  }, [
    defaultPageAfterRegistration,
    clientProfilePath,
    history,
    isMultiStepFlow,
    isFirstStep,
    leaveAfterFirstStep,
  ]);

  const action = useSignUpAction({
    activeStep,
    isFirstStep,
    isLastStep,
    isMultiStepFlow,
    leaveAfterFirstStep,
    onNextStep: handleNextStep,
    redirectAfterRegistration,
    resetActiveForm,
    setActiveStep,
    stepSubmit,
    submit,
    useWindowRedirect: true,
  });

  const formFields = activeType?.steps[activeStep]?.fields;
  const realFormFields = useMemo(() => {
    if (formFields) {
      return formFields.map(field => ({
        ...field,
        ...customSignupFormRules(field),
        ...changeAsyncToDeferRules(field),
      }));
    }
    return formFields;
  }, [formFields]);

  const socialProviders = useSocialProviders(activeType);

  const shouldShowStepper = !isMultiStepFlow || isShownProgressBar;
  const flowType = isMultiStepFlow ? OAUTH_FLOW_TYPES.SIGNUP_MULTISTEP : OAUTH_FLOW_TYPES.SIGNUP;

  const renderForm = activeType ? (
    <SignUpFormBody
      action={action}
      activeStep={activeStep}
      activeType={activeType}
      fields={realFormFields}
      flowType={flowType}
      formRef={formRef}
      hideSocialProviders={isOAuthRegistrationRef.current}
      isBackButtonVisible={isBackButtonVisible}
      isLastStep={isLastStep}
      onBackButtonClick={onBackButtonClick}
      onTrackedFieldChanges={gtmFieldChangesHandler}
      shouldShowStepper={shouldShowStepper}
      showSignInLink={showSignInLink}
      signInContainerClassName="simple-signin-footer"
      signInContent={
        <div>
          <Label className="title-300-text" message="Already member" />
          <a href="/signin">
            <Label className="title-bold-text link" message="Sign in" />
          </a>
        </div>
      }
      socialProviders={socialProviders}
      stepsAmount={stepsAmount}
      trackedFields={GTM_TRACKED_FIELDS}
    />
  ) : null;

  return (
    <>
      {signUpPopup ? (
        <DefaultPopup
          className="signup-popup-wrapper"
          headerIcon={<Logo />}
          headerIconPosition="left"
          onClose={closeSignupPopup}
        >
          <StaticBlock
            className="signup-popup-static-block"
            options={{ source: 'mobile_signup_banner' }}
          />
          <HeaderTitle title={headerTitle} />
          {Array.isArray(types) && types.length <= 1 ? null : (
            <Navigation activeType={activeType} onChange={setActiveType} types={types} />
          )}
          <div className="popup__content">{renderForm}</div>
        </DefaultPopup>
      ) : (
        <div className="auth sign-up">
          <div className="content">
            <HeaderTitle title="registration" />
            <StaticBlock options={{ source: 'mobile_signup_banner' }} />
            {Array.isArray(types) && types.length <= 1 ? null : (
              <Navigation activeType={activeType} onChange={setActiveType} types={types} />
            )}
            {renderForm}
          </div>
        </div>
      )}
    </>
  );
};

SignUp.propTypes = {};

export default SignUp;
